export enum TransactionOutcome {
  OK_ACCEPTED = "OK-ACCEPTED", // The transaction was accepted.
  OK_DECLINED = "OK-DECLINED", // The transaction was declined.
  OK_UNAVAILABLE = "OK-UNAVAILABLE", // The transaction could not be processed at this time.
  OK_DELAYED = "OK-DELAYED", // The transaction is taking longer than expected to be completed (while the transaction is still pending).
  CANCELLED = "CANCELLED", // The transaction was cancelled by the operator or customer, or by the Payment Acceptance Device.
  FAILED = "FAILED", // The transaction failed.
  FAILED_INTERFACE = "FAILED-INTERFACE", // The transaction failed because of a communications or interface error.
  DeviceOffline = "DEVICE-OFFLINE", // The Payment Acceptance Device is offline.
}

export enum TransactionFunctionResult {
  OK = "OK", // The function was processed successfully.
  CANCELLED = "CANCELLED", // The function was cancelled.
  DELAYED_TRANSACTION = "DELAYED-TRANSACTION", // The function is taking longer than expected to be completed (pending).
  FAILED = "FAILED", // The function failed.
  FAILED_INTERFACE = "FAILED-INTERFACE", // The function failed due to a communications/interface error.
}

export enum TransactionType {
  CardPurchase = "Card.Purchase", // A card purchase transaction.
  CardRefund = "Card.Refund", // A card refund transaction.
}

export enum TransactionStatus {
  Pending = "PENDING", // The transaction is pending and has not yet been completed.
  Completed = "COMPLETED", // The transaction has been completed successfully.
}

export type TransactionRequestResponse = {
  transactionId: string; // Unique identifier for the transaction
  transactionTimeStamp: string; // ISO 8601 timestamp of the transaction (UTC)
  merchantId: string; // Unique identifier for the merchant
  deviceId: string; // Unique identifier for the device
  transactionStatus: TransactionStatus; // Status of the transaction
  data?: TransactionDataObject; //data object containing additional transaction details
};

export type TransactionDataObject = {
  TransactionResult?: TransactionOutcome; // Outcome of the transaction
  Receipt?: string; // Receipt data in base64 format
  RequestId?: string; // Unique identifier for the request
  AcquirerRef?: string; // Reference from the acquirer
  AccountType?: string; // Type of account used in the transaction
  Timestamp?: string; // ISO 8601 timestamp of the transaction (UTC)
  Result: TransactionFunctionResult; // Result of the transaction function
  ResultText?: string; // Text description of the result
  Function: TransactionType; // Function that was executed
  AuthId?: string; // Authorization ID for the transaction
  CardPan?: string; // Primary Account Number (PAN) of the card used
  AmountTotal?: string;
  Merchant?: string;
  CardType?: string; // Type of card used in the transaction
  TeminalRef?: string; // Reference for the terminal
  AmountSurcharge?: string; // Amount of surcharge included in the transaction
  AmountTip?: string; // Amount of tip included in the transaction
  PollingUrl?: string; // URL for polling the transaction status
};

export enum TransactionPollingStatus {
  Pending = "PENDING", // The transaction is still being processed.
  Completed = "COMPLETED", // The transaction has been completed.
}

export type PairingRequestInput = {
  POSRegisterID: string;
  POSRegisterName: string;
  POSBusinessName: string;
  POSVendorName: string;
  ContactName?: string;
  Address?: string;
  City?: string;
  State?: string;
  ZipCode?: string;
  Email?: string;
  Phone?: string;
};

export type PairingRequestResponse =
  | {
      result: "success";
    }
  | {
      error: string;
    };

export type PollForOutcomeFn = (
  pollingUrl: string
) => Promise<TransactionRequestResponse>;

export type PairingRequestFn = (
  pairingCode: string,
  options: {
    input: PairingRequestInput;
  }
) => Promise<PairingRequestResponse>;

export type CreateTransactionFn = (
  amountInCents: number,
  transactionType: TransactionType,
  metadata: {
    registerId: string; // Unique identifier for the register
    posBusinessName: string; // Name of the business
    posVendorName: string; // Name of the vendor
  }
) => Promise<
  | TransactionRequestResponse
  | {
      error: string;
    }
>;

export enum TransactionMode {
  Async = "ASYNC", // The transaction is processed asynchronously.
}
